import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import React, { ComponentType, forwardRef, memo, MemoExoticComponent, useCallback } from 'react';

import AlertGlobalProvider, { showGlobalAlert } from './global';
import { useTranslation } from 'react-i18next';

interface IProps {
  opened: boolean;
  message: React.ReactNode;
  title?: string;
  confirmation?: boolean;
  global?: boolean;
  onClose: (ok: boolean) => void;
}

export interface IAlertShowParams {
  message: React.ReactNode;
  title?: string;
  confirmation?: boolean;
}

type AlertComponent = MemoExoticComponent<ComponentType<IProps>> & {
  Global?: typeof AlertGlobalProvider;

  show?(params: string): Promise<boolean>;
  show?(params: IAlertShowParams): Promise<boolean>;

  confirm?(params: string): Promise<boolean>;
  confirm?(params: IAlertShowParams): Promise<boolean>;
};

const Alert: AlertComponent = memo((props: IProps) => {
  const { t } = useTranslation();

  const handleOk = useCallback(() => props.onClose(true), [props]);
  const handleCancel = useCallback(() => props.onClose(false), [props]);

  return (
    <Dialog open={props.opened} keepMounted TransitionComponent={Transition} onClose={handleCancel} style={{ zIndex: 1600}}>
      <DialogTitle>{props.title || (props.confirmation ? t('APP_ADMIN_DIALOG_CONFIRMATION') : t('APP_ADMIN_DIALOG_ATTENTION'))}</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ minWidth: '250px'}}>{props.message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {props.confirmation && (
          <Button onClick={handleCancel} autoFocus>
            {t('APP_ADMIN_DIALOG_CANCEL')}
          </Button>
        )}
        <Button autoFocus={!props.confirmation} onClick={handleOk} color='primary'>
          {t('APP_ADMIN_DIALOG_CONFIRM')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

Alert.Global = AlertGlobalProvider;

Alert.show = (params: string | IAlertShowParams) => {
  const paramsData = typeof params === 'string' ? { message: params } : params;
  return showGlobalAlert(paramsData);
};

Alert.confirm = (params: string | IAlertShowParams) => {
  const paramsData = typeof params === 'string' ? { message: params } : params;
  return showGlobalAlert({ ...paramsData, confirmation: true });
};

const Transition = memo(
  forwardRef((props: any, ref: any) => {
    return <Slide direction='up' {...props} ref={ref} />;
  })
);

export default Alert;
